import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Settings, User, KeyRound, LogOut, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to truncate long names
const formatDisplayName = (name, maxLength = 20) => {
  if (!name) return "Benutzer";
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength - 2) + "...";
};

const SettingsDropdown = ({ user: propUser }) => {
  const navigate = useNavigate();
  const { user: authUser, logout } = useAuth();
  const user = propUser || authUser;
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  const displayName = user?.name || user?.username || "Benutzer";
  const isLongName = displayName.length > 15;

  const openProfileDialog = () => {
    setFormData({
      name: user?.name || "",
      email: user?.email || "",
      password: "",
      confirmPassword: ""
    });
    setProfileDialogOpen(true);
  };

  const handleSaveProfile = async () => {
    if (formData.password && formData.password !== formData.confirmPassword) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }
    if (formData.password && formData.password.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben");
      return;
    }

    setSaving(true);
    try {
      const payload = {};
      if (formData.name && formData.name !== user?.name) {
        payload.name = formData.name;
      }
      if (formData.email !== user?.email) {
        payload.email = formData.email;
      }
      if (formData.password) {
        payload.password = formData.password;
      }

      if (Object.keys(payload).length === 0) {
        toast.info("Keine Änderungen vorgenommen");
        setProfileDialogOpen(false);
        return;
      }

      await axios.put(`${API}/auth/profile`, payload, { withCredentials: true });
      toast.success("Profil aktualisiert");
      setProfileDialogOpen(false);
      // Refresh page to update user data
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="settings-dropdown-trigger centered-profile"
            data-testid="settings-dropdown-btn"
            title={`Einstellungen - ${displayName}`}
          >
            <div className="profile-avatar">
              <User size={16} />
            </div>
            <span className={`profile-name ${isLongName ? 'text-xs' : 'text-sm'}`}>
              {formatDisplayName(displayName)}
            </span>
            <ChevronDown size={14} className="opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-56">
          {/* User Info Header */}
          <div className="px-3 py-2 border-b border-slate-700">
            <p className={`font-medium truncate text-white ${displayName.length > 20 ? 'text-xs' : 'text-sm'}`}>
              {displayName}
            </p>
            <p className="text-xs text-slate-400 truncate">{user?.email || user?.username}</p>
          </div>

          <DropdownMenuItem
            onClick={openProfileDialog}
            className="cursor-pointer"
            data-testid="edit-profile-btn"
          >
            <User size={16} className="mr-2" />
            Profil bearbeiten
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => navigate("/einstellungen")}
            className="cursor-pointer"
            data-testid="goto-settings-btn"
          >
            <Settings size={16} className="mr-2" />
            Einstellungen
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={handleLogout}
            className="cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
            data-testid="dropdown-logout-btn"
          >
            <LogOut size={16} className="mr-2" />
            Abmelden
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Profile Edit Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User size={20} className="text-blue-500" />
              Profil bearbeiten
            </DialogTitle>
            <DialogDescription>
              Ändern Sie Ihre persönlichen Daten
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ihr Name"
                data-testid="profile-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">E-Mail</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="ihre@email.de"
                data-testid="profile-email-input"
              />
            </div>

            <div className="border-t pt-4">
              <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
                <KeyRound size={12} />
                Passwort ändern (optional)
              </p>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Neues Passwort</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Mindestens 6 Zeichen"
                    data-testid="profile-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Passwort bestätigen</Label>
                  <Input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Passwort wiederholen"
                    data-testid="profile-confirm-password-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setProfileDialogOpen(false)}
              >
                Abbrechen
              </Button>
              <Button
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                onClick={handleSaveProfile}
                disabled={saving}
                data-testid="profile-save-btn"
              >
                {saving ? "Speichern..." : "Speichern"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SettingsDropdown;
